/**
 * YouTubeEmbed — native-safe YouTube video tile.
 *
 * Renders a lightweight thumbnail + play button. Behavior on tap depends
 * on the platform:
 *
 *   • Web / PWA: mounts the standard `youtube-nocookie.com/embed/{id}`
 *     iframe inline so the video plays in-page. Uses `playsinline=1`
 *     for iOS Safari.
 *
 *   • Native (Capacitor iOS shell): NEVER mounts an iframe. YouTube's
 *     embed player refuses to load inside `capacitor://localhost` and
 *     shows "Video player configuration error / Error 153". Instead we
 *     open the video externally — first trying the native YouTube app
 *     via `youtube://` deep link (through `@capacitor/app-launcher`),
 *     then falling back to SFSafariViewController via
 *     `@capacitor/browser` if YouTube isn't installed.
 *
 * Props:
 *   - videoId    (string, required)  YouTube 11-char video ID
 *   - watchUrl   (string, optional)  Full watch URL — used for
 *                                    external open and the "Open on
 *                                    YouTube" escape link. If omitted
 *                                    we build it from `videoId`.
 *   - title      (string, optional)  A11y label + iframe title
 *   - autoplay   (bool,   optional)  Whether the web iframe should
 *                                    autoplay on mount. Default false.
 *   - onPlay     (fn,     optional)  Fired the moment the user taps
 *                                    play — used for analytics.
 *   - showBadge  (bool,   optional)  Show the little YouTube pill in
 *                                    the corner. Default true.
 *   - showOpenLink (bool, optional)  Show the "Open on YouTube" link
 *                                    in the bottom-right. Default true.
 *   - className  (string, optional)  Extra classes for the outer wrap.
 */
import { useState } from 'react';
import { Play, Youtube, ExternalLink } from 'lucide-react';
import { isNativeApp, openExternalLink } from '../lib/nativeBridge';

const YouTubeEmbed = ({
  videoId,
  watchUrl,
  title,
  autoplay = false,
  onPlay,
  showBadge = true,
  showOpenLink = true,
  compact = false,
  className = '',
}) => {
  const [playing, setPlaying] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);

  if (!videoId) return null;

  const watch = watchUrl || `https://www.youtube.com/watch?v=${videoId}`;
  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const native = isNativeApp();

  const handlePlay = () => {
    onPlay?.();
    if (native) {
      // Deep-link to the native YouTube app; falls back to Safari sheet.
      openExternalLink(watch, `youtube://${videoId}`);
      return;
    }
    setPlaying(true);
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-video bg-black ${className}`}
      data-testid="youtube-embed"
    >
      {playing && !native ? (
        <iframe
          title={title || 'YouTube video'}
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay || playing ? 1 : 0}&rel=0&playsinline=1&modestbranding=1&iv_load_policy=3`}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          data-testid="youtube-embed-iframe"
        />
      ) : (
        <button
          type="button"
          onClick={handlePlay}
          className="group absolute inset-0 w-full h-full flex items-center justify-center"
          aria-label={title ? `Play ${title}` : 'Play video'}
          data-testid="youtube-embed-play"
        >
          {!thumbBroken && (
            <img
              src={thumb}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setThumbBroken(true)}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
          <span className={`relative inline-flex items-center justify-center rounded-full bg-[#FF0033] text-white shadow-2xl group-hover:scale-105 transition ${compact ? 'w-10 h-10' : 'w-16 h-16'}`}>
            <Play className={compact ? 'w-4 h-4 ml-0.5' : 'w-6 h-6 ml-0.5'} fill="currentColor" />
          </span>
          {showBadge && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-white bg-black/60 rounded-full px-2 py-0.5">
              <Youtube className="w-2.5 h-2.5" /> YouTube
            </span>
          )}
        </button>
      )}
      {showOpenLink && (
        <a
          href={watch}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // On native, hijack the anchor and route through the deep
            // link so we skip the browser's junk "open in a new page"
            // popup and go straight to the YouTube app or Safari sheet.
            if (native) {
              e.preventDefault();
              e.stopPropagation();
              openExternalLink(watch, `youtube://${videoId}`);
            } else {
              e.stopPropagation();
            }
          }}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-white bg-black/70 hover:bg-black/90 backdrop-blur rounded-full px-2.5 py-1 ring-1 ring-white/10"
          data-testid="youtube-embed-open"
        >
          Open on YouTube <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
    </div>
  );
};

export default YouTubeEmbed;
